import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { playClick, playHit, playOut, playHomeRun } from '@/utils/sounds';
import { AvatarCanvasSmall } from '@/components/AvatarCanvas';
import {
  RetroButton,
  bandedGradient,
  pixelStripes,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESULT_LETTERS = { SINGLE: '1B', DOUBLE: '2B', TRIPLE: '3B', HOME_RUN: 'HR', OUT: 'OUT' };
const RESULT_NAMES = { SINGLE: 'SINGLE', DOUBLE: 'DOUBLE', TRIPLE: 'TRIPLE', HOME_RUN: 'HOME RUN', OUT: 'OUT' };

const led = (size, color = PALETTE.scoreboardText) => ({
  fontFamily: DESIGN_TOKENS.fonts.display,
  fontSize: size,
  color,
  textShadow: `0 0 8px ${color}88`,
});

function secondsUntil(iso) {
  if (!iso) return null;
  const dl = new Date(iso).getTime();
  return Math.max(0, Math.ceil((dl - Date.now()) / 1000));
}

/* Persistent LED scoreboard: score, inning+half, outs, diamond */
export function OnlineScoreboard({ teamA, teamB, gameState, override }) {
  const inning = override?.inning ?? gameState?.inning ?? 1;
  const half = override?.half ?? gameState?.half ?? 'top';
  const outs = override?.outs ?? gameState?.outs ?? 0;
  const bases = override?.bases ?? gameState?.bases ?? {};
  const scoreA = override?.scoreA ?? teamA?.score ?? 0;
  const scoreB = override?.scoreB ?? teamB?.score ?? 0;

  const Base = ({ on, style }) => (
    <div style={{
      position: 'absolute',
      width: '14px',
      height: '14px',
      transform: 'rotate(45deg)',
      background: on ? ACCENTS.gold : 'transparent',
      border: `2px solid ${on ? ACCENTS.gold : `${PALETTE.scoreboardText}66`}`,
      boxShadow: on ? `0 0 8px ${ACCENTS.gold}` : 'none',
      ...style,
    }} />
  );

  return (
    <div
      className="mb-3 px-3 py-2"
      style={{
        background: PALETTE.scoreboardBg,
        border: `4px solid ${PALETTE.ink}`,
        borderRadius: DESIGN_TOKENS.borderRadius.md,
        boxShadow: `4px 4px 0px rgba(0,0,0,0.4)`,
      }}
      data-testid="online-scoreboard"
    >
      <div className="flex items-center justify-between gap-2">
        {/* Team A */}
        <div className="text-center" style={{ minWidth: '70px' }}>
          <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '10px', color: `${PALETTE.cream}99`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>
            {teamA?.name || 'TEAM A'}
          </p>
          <p style={led('22px')} data-testid="scoreboard-score-a">{scoreA}</p>
        </div>

        {/* Diamond */}
        <div style={{ position: 'relative', width: '58px', height: '46px' }} data-testid="scoreboard-diamond">
          <Base on={bases.second} style={{ top: '2px', left: '22px' }} />
          <Base on={bases.third} style={{ top: '20px', left: '4px' }} />
          <Base on={bases.first} style={{ top: '20px', left: '40px' }} />
        </div>

        {/* Inning + outs */}
        <div className="text-center">
          <p style={led('12px', '#FFFFFF')} data-testid="scoreboard-inning">
            {half === 'top' ? 'TOP' : 'BOT'} {inning}
          </p>
          <p style={led('11px', '#FF3B3B')} data-testid="scoreboard-outs">
            {outs} OUT{outs === 1 ? '' : 'S'}
          </p>
        </div>

        {/* Team B */}
        <div className="text-center" style={{ minWidth: '70px' }}>
          <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '10px', color: `${PALETTE.cream}99`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>
            {teamB?.name || 'TEAM B'}
          </p>
          <p style={led('22px')} data-testid="scoreboard-score-b">{scoreB}</p>
        </div>
      </div>
    </div>
  );
}

/* Black "NOW BATTING" bar with avatar chip */
function NowBattingBar({ batterName, batterAvatar }) {
  return (
    <div
      className="flex items-center justify-center gap-2 px-3 py-2 mb-3"
      style={{
        background: PALETTE.scoreboardBg,
        border: `3px solid ${PALETTE.ink}`,
        borderRadius: DESIGN_TOKENS.borderRadius.md,
      }}
      data-testid="online-now-batting"
    >
      <span style={{ fontFamily: DESIGN_TOKENS.fonts.display, fontSize: '9px', color: '#FFFFFF', letterSpacing: '1px' }}>
        NOW BATTING
      </span>
      {batterAvatar && Object.keys(batterAvatar).length > 0 && (
        <span className="inline-flex"><AvatarCanvasSmall config={batterAvatar} size={24} /></span>
      )}
      <span style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 700, fontSize: '14px', color: ACCENTS.gold }}>
        {batterName || '...'}
      </span>
    </div>
  );
}

/* LED countdown timer chip */
function TimerChip({ seconds, testId }) {
  const danger = seconds !== null && seconds <= 5;
  return (
    <div
      className="inline-block px-4 py-1 mb-3"
      style={{
        background: PALETTE.scoreboardBg,
        border: `3px solid ${PALETTE.ink}`,
        borderRadius: DESIGN_TOKENS.borderRadius.sm,
      }}
      data-testid={testId}
    >
      <span style={led('18px', danger ? '#FF3B3B' : PALETTE.scoreboardText)}>
        {seconds !== null ? `0:${String(seconds).padStart(2, '0')}` : '--'}
      </span>
    </div>
  );
}

/* Question category with 4 tap options */
function QuestionCategory({ label, options, selected, onSelect }) {
  return (
    <div className="mb-3">
      <p style={{
        fontFamily: DESIGN_TOKENS.fonts.display,
        fontSize: '10px',
        color: PALETTE.grass,
        letterSpacing: '1px',
        marginBottom: '6px',
      }}>
        {label}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((opt) => {
          const isSel = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              className="px-2 py-2 transition-all"
              style={{
                border: `2px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.md,
                background: isSel
                  ? bandedGradient(ACCENTS.goldLight, ACCENTS.gold, ACCENTS.goldDark)
                  : bandedGradient('#FFFFFF', '#FFFFFF', PALETTE.cream),
                boxShadow: isSel ? `inset 1px 1px 0px #FFF3C4, 1px 1px 0px ${PALETTE.ink}` : `1px 1px 0px ${PALETTE.ink}`,
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontWeight: isSel ? 700 : 600,
                fontSize: '12px',
                color: PALETTE.ink,
                lineHeight: 1.25,
              }}
              data-testid={`answer-${label.toLowerCase()}-${opt.value.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
            >
              {opt.value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function OnlineGame({ matchId, match, gameMsg, getAuthHeader, refetchMatch }) {
  const [atBat, setAtBat] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [halfEnd, setHalfEnd] = useState(null);
  const [gameEnd, setGameEnd] = useState(null);
  const [autoPicked, setAutoPicked] = useState(false);

  const resultUntilRef = useRef(0);
  const startThrottleRef = useRef(0);
  const resolveThrottleRef = useRef(0);
  const autoPickRef = useRef(null);
  const audioRef = useRef(null);
  const audioKeyRef = useRef(null);
  const atBatKeyRef = useRef(null);
  const mountedRef = useRef(true);

  const headers = getAuthHeader();

  // Fetch at-bat state (source of truth)
  const fetchAtBat = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/online/match/${matchId}/at-bat`, { headers });
      const data = await res.json();
      if (!mountedRef.current) return;
      setAtBat(data);
    } catch (e) {
      // keep polling
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchAtBat();
    const poll = setInterval(fetchAtBat, 2000);
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => {
      mountedRef.current = false;
      clearInterval(poll);
      clearInterval(tick);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [fetchAtBat]);

  // React to WS gameplay messages
  useEffect(() => {
    if (!gameMsg) return;
    const { type, payload } = gameMsg;
    if (type === 'at_bat_result') {
      setResult(payload);
      setHalfEnd(null);
      resultUntilRef.current = Date.now() + 6000;
      if (payload.forfeit_out) toast.info('Batter unavailable — automatic out');
      if (payload.result === 'OUT') playOut();
      else if (payload.result === 'HOME_RUN') playHomeRun();
      else playHit();
      refetchMatch();
      setAtBat(null);
      setAnswers({});
      setSubmitted(false);
      setAutoPicked(false);
    } else if (type === 'half_inning_end') {
      setHalfEnd(payload);
      refetchMatch();
    } else if (type === 'game_end') {
      setGameEnd(payload);
      refetchMatch();
    } else {
      // pitcher_turn / song_selected / batter_turn -> refresh at-bat state
      if (type === 'song_selected' && payload?.auto_selected) {
        toast.info('Pitcher unavailable — song auto-selected');
      }
      fetchAtBat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMsg]);

  const isActive = atBat && atBat.batter_user_id;
  const inResultWindow = Date.now() < resultUntilRef.current;

  // Reset per-at-bat local state when a new at-bat appears
  useEffect(() => {
    if (!isActive) return;
    const key = `${atBat.batter_user_id}-${atBat.pitcher_user_id}-${atBat.deadline || ''}`;
    if (atBatKeyRef.current !== `${atBat.batter_user_id}-${atBat.pitcher_user_id}`) {
      atBatKeyRef.current = `${atBat.batter_user_id}-${atBat.pitcher_user_id}`;
      setAnswers({});
      setSubmitted(atBat.already_answered || false);
      setAutoPicked(false);
    } else if (atBat.already_answered && !submitted) {
      setSubmitted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, atBat?.batter_user_id, atBat?.pitcher_user_id, atBat?.already_answered]);

  // Orchestrate: start next at-bat when idle (any client, race-safe)
  useEffect(() => {
    if (gameEnd || inResultWindow) return;
    if (match?.state !== 'in_progress') return;
    if (atBat && !atBat.batter_user_id && atBat.role === 'waiting') {
      if (Date.now() - startThrottleRef.current > 3000) {
        startThrottleRef.current = Date.now();
        fetch(`${API_URL}/api/online/match/${matchId}/start-at-bat`, {
          method: 'POST', headers,
        }).then(() => fetchAtBat()).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atBat, now, gameEnd]);

  // Pitcher auto-pick on timeout
  useEffect(() => {
    if (!isActive || atBat.role !== 'pitching' || atBat.phase !== 'waiting_for_pitch') return;
    const secs = secondsUntil(atBat.deadline);
    if (secs === 0 && atBat.available_songs?.length > 0 && autoPickRef.current !== atBatKeyRef.current) {
      autoPickRef.current = atBatKeyRef.current;
      const random = atBat.available_songs[Math.floor(Math.random() * atBat.available_songs.length)];
      setAutoPicked(true);
      selectSong(random.song_id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, isActive]);

  // Resolve on batter deadline timeout (any client, race-safe)
  useEffect(() => {
    if (!isActive || atBat.phase !== 'answering' || gameEnd) return;
    const dl = atBat.deadline ? new Date(atBat.deadline).getTime() : null;
    if (dl && Date.now() > dl + 2000 && Date.now() - resolveThrottleRef.current > 5000) {
      resolveThrottleRef.current = Date.now();
      fetch(`${API_URL}/api/online/match/${matchId}/resolve-at-bat`, {
        method: 'POST', headers,
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, isActive]);

  // Server-coordinated audio playback
  useEffect(() => {
    if (!isActive || !atBat.preview_url || !atBat.audio_start_at) return;
    const key = atBat.preview_url + atBat.audio_start_at;
    if (audioKeyRef.current === key) return;
    audioKeyRef.current = key;
    if (audioRef.current) { audioRef.current.pause(); }
    const audio = new Audio(atBat.preview_url);
    audioRef.current = audio;
    const delay = new Date(atBat.audio_start_at).getTime() - Date.now();
    const play = () => audio.play().catch(() => {});
    if (delay > 0) setTimeout(play, delay);
    else play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, atBat?.preview_url, atBat?.audio_start_at]);

  // Stop audio when result arrives
  useEffect(() => {
    if (result && audioRef.current) { audioRef.current.pause(); }
  }, [result]);

  const selectSong = async (songId, isAuto = false) => {
    if (!isAuto) playClick();
    try {
      const res = await fetch(`${API_URL}/api/online/match/${matchId}/select-song`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ song_id: songId }),
      });
      if (!res.ok) throw new Error();
      fetchAtBat();
    } catch (e) {
      if (!isAuto) toast.error('Song selection failed');
    }
  };

  const submitAnswers = async () => {
    playClick();
    setSubmitted(true);
    try {
      const res = await fetch(`${API_URL}/api/online/match/${matchId}/submit-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          title_answer: answers.title || null,
          artist_answer: answers.artist || null,
          album_answer: answers.album || null,
          year_answer: answers.year || null,
        }),
      });
      if (!res.ok) throw new Error();
      // Batter triggers immediate resolution
      if (atBat.role === 'batting') {
        fetch(`${API_URL}/api/online/match/${matchId}/resolve-at-bat`, {
          method: 'POST', headers,
        }).catch(() => {});
      }
    } catch (e) {
      setSubmitted(false);
      toast.error('Failed to submit answers');
    }
  };

  // Lookups
  const findSlot = (userId) => {
    for (const team of [match?.team_a, match?.team_b]) {
      for (const slot of team?.slots || []) {
        if (slot.user_id === userId) return slot;
      }
    }
    return null;
  };
  const batterSlot = isActive ? findSlot(atBat.batter_user_id) : null;
  const secs = isActive ? secondsUntil(atBat.deadline) : null;

  const panelStyle = {
    background: PALETTE.cream,
    border: `4px solid ${PALETTE.ink}`,
    borderRadius: DESIGN_TOKENS.borderRadius.lg,
    boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
  };

  /* ============ RENDER ============ */

  const scoreboardOverride = (result && inResultWindow) ? {
    outs: result.outs,
    bases: result.bases,
    scoreA: result.score?.team_a,
    scoreB: result.score?.team_b,
  } : null;

  return (
    <div data-testid="online-game">
      <OnlineScoreboard
        teamA={match?.team_a}
        teamB={match?.team_b}
        gameState={match?.game_state}
        override={scoreboardOverride}
      />

      {/* GAME OVER (simple - full endgame screens next phase) */}
      {gameEnd ? (
        <div className="text-center p-5" style={panelStyle} data-testid="online-game-over">
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displayLg,
            color: '#E23B2E',
            textShadow: `2px 2px 0px ${PALETTE.ink}`,
            letterSpacing: '2px',
            marginBottom: '12px',
          }}>
            GAME OVER
          </p>
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: '14px',
            color: PALETTE.ink,
            marginBottom: '8px',
          }}>
            {gameEnd.final_score?.team_a} - {gameEnd.final_score?.team_b}
          </p>
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontWeight: 700,
            fontSize: '15px',
            color: PALETTE.grass,
          }} data-testid="online-winner">
            {gameEnd.winner === 'TIE' ? "IT'S A TIE!" : `${gameEnd.winner === 'A' ? gameEnd.team_a_name : gameEnd.team_b_name} WINS!`}
          </p>
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: PALETTE.dirt,
            marginTop: '12px',
          }}>
            Full scorecard &amp; Peanut reveal coming in the next build phase
          </p>
        </div>
      ) : inResultWindow && result ? (
        /* RESULT VIEW - shown to everyone for 6s */
        <div className="text-center p-4" style={panelStyle} data-testid="online-result-view">
          {halfEnd && (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '10px',
              color: PALETTE.dirt,
              letterSpacing: '1px',
              marginBottom: '8px',
            }} data-testid="side-retired-banner">
              SIDE RETIRED - {halfEnd.new_half === 'top' ? 'TOP' : 'BOTTOM'} OF {halfEnd.new_inning} NEXT
            </p>
          )}
          <div
            className="mb-3"
            style={{
              background: PALETTE.scoreboardBg,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              padding: '16px 12px',
            }}
          >
            {(() => {
              const rSlot = result.batter_user_id ? findSlot(result.batter_user_id) : null;
              return rSlot ? (
                <div className="flex items-center justify-center gap-2 mb-2" data-testid="result-batter-chip">
                  {rSlot.avatar_config && Object.keys(rSlot.avatar_config).length > 0 && (
                    <span className="inline-flex"><AvatarCanvasSmall config={rSlot.avatar_config} size={24} /></span>
                  )}
                  <span style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 700, fontSize: '13px', color: ACCENTS.gold }}>
                    {rSlot.username}
                  </span>
                </div>
              ) : null;
            })()}
            <div style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '40px',
              lineHeight: 1,
              color: result.result === 'OUT' ? '#FF3B3B' : ACCENTS.gold,
              textShadow: result.result === 'OUT' ? '0 0 14px rgba(255,59,59,0.6)' : '0 0 14px rgba(242,196,56,0.6)',
              marginBottom: '10px',
            }} data-testid="online-result-letters">
              {RESULT_LETTERS[result.result] || result.result}
            </div>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '13px',
              color: '#FFFFFF',
              letterSpacing: '2px',
              marginBottom: result.runs_scored > 0 ? '8px' : 0,
            }}>
              {result.is_grand_slam ? 'GRAND SLAM!' : (RESULT_NAMES[result.result] || result.result)}
            </p>
            {result.runs_scored > 0 && (
              <p style={led('12px')}>
                +{result.runs_scored} RUN{result.runs_scored > 1 ? 'S' : ''}!
              </p>
            )}
          </div>
          {result.correct_answers?.title && (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '13px',
              color: PALETTE.ink,
            }} data-testid="song-reveal">
              ♪ {result.correct_answers.title} — {result.correct_answers.artist} ({result.correct_answers.year})
            </p>
          )}
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '11px',
            color: `${PALETTE.ink}77`,
            marginTop: '8px',
          }}>
            Next batter coming up...
          </p>
        </div>
      ) : !isActive ? (
        /* IDLE - between at-bats */
        <div className="text-center p-6" style={panelStyle} data-testid="online-idle-view">
          <p className="animate-pulse" style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontWeight: 600,
            fontSize: '14px',
            color: PALETTE.ink,
          }}>
            Setting up the next at-bat...
          </p>
        </div>
      ) : atBat.role === 'pitching' && atBat.phase === 'waiting_for_pitch' ? (
        /* PITCHER: SONG SELECTION */
        <div className="text-center p-4" style={panelStyle} data-testid="pitcher-select-view">
          <NowBattingBar batterName={batterSlot?.username} batterAvatar={batterSlot?.avatar_config} />
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: '13px',
            color: PALETTE.ink,
            letterSpacing: '1px',
            marginBottom: '4px',
          }}>
            YOU ARE PITCHING
          </p>
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: PALETTE.dirt,
            marginBottom: '10px',
          }}>
            Pick a song to stump the batter!
          </p>
          <TimerChip seconds={secs} testId="pitcher-timer" />
          {autoPicked && (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '12px',
              color: '#C0392B',
              marginBottom: '8px',
            }} data-testid="auto-pick-note">
              Time&apos;s up! Random song selected...
            </p>
          )}
          <div className="space-y-2">
            {(atBat.available_songs || []).map((song) => (
              <button
                key={song.song_id}
                onClick={() => selectSong(song.song_id)}
                className="w-full px-4 py-2 text-left transition-all"
                style={{
                  border: `3px solid ${PALETTE.ink}`,
                  borderRadius: DESIGN_TOKENS.borderRadius.md,
                  background: bandedGradient('#FFFFFF', '#FFFFFF', PALETTE.cream),
                  boxShadow: `2px 2px 0px ${PALETTE.ink}`,
                }}
                data-testid={`song-option-${song.song_id}`}
              >
                <span style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontWeight: 700,
                  fontSize: '13px',
                  color: PALETTE.ink,
                  display: 'block',
                }}>
                  {song.title}
                </span>
                <span style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '11px',
                  color: `${PALETTE.ink}99`,
                }}>
                  {song.artist} • {song.year}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : atBat.role === 'pitching' ? (
        /* PITCHER: HOLD STATE while others answer */
        <div className="text-center p-4" style={panelStyle} data-testid="pitcher-hold-view">
          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: '#C6BBA5',
              backgroundImage: pixelStripes('C6BBA5', 'B6AA92'),
              backgroundSize: '48px 48px',
              border: `3px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
            }}
          >
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '12px',
              color: PALETTE.ink,
              letterSpacing: '1px',
              marginBottom: '6px',
            }}>
              PITCH THROWN!
            </p>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 700,
              fontSize: '13px',
              color: PALETTE.ink,
            }}>
              ♪ YOU ARE PITCHING - HOLD THE PHONE ♪
            </p>
          </div>
          <NowBattingBar batterName={batterSlot?.username} batterAvatar={batterSlot?.avatar_config} />
          <TimerChip seconds={secs} testId="pitcher-hold-timer" />
          <p className="animate-pulse" style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: PALETTE.dirt,
          }}>
            The batter is guessing your song...
          </p>
        </div>
      ) : atBat.phase === 'waiting_for_pitch' ? (
        /* BATTER/GALLERY: waiting for pitcher */
        <div className="text-center p-4" style={panelStyle} data-testid="waiting-for-pitch-view">
          <NowBattingBar batterName={batterSlot?.username} batterAvatar={batterSlot?.avatar_config} />
          {atBat.role === 'batting' && (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '13px',
              color: PALETTE.grass,
              letterSpacing: '1px',
              marginBottom: '8px',
            }} data-testid="you-are-up-note">
              YOU&apos;RE UP!
            </p>
          )}
          <p className="animate-pulse" style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontWeight: 600,
            fontSize: '13px',
            color: PALETTE.ink,
          }}>
            Waiting for the pitcher to choose a song...
          </p>
        </div>
      ) : submitted ? (
        /* ANSWERS LOCKED (batter waits for result; gallery gets silent confirm) */
        <div className="text-center p-5" style={panelStyle} data-testid="answers-locked-view">
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: '13px',
            color: PALETTE.grass,
            letterSpacing: '1px',
            marginBottom: '8px',
          }}>
            {atBat.role === 'batting' ? 'SWING TAKEN!' : 'ANSWERS LOCKED IN'}
          </p>
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: PALETTE.dirt,
          }}>
            {atBat.role === 'batting'
              ? 'Waiting for the call...'
              : 'Peanut points stay secret until the end of the game'}
          </p>
        </div>
      ) : (
        /* BATTER / PEANUT GALLERY: answering */
        <div className="p-4" style={panelStyle} data-testid={atBat.role === 'batting' ? 'batter-answer-view' : 'peanut-answer-view'}>
          <div className="text-center">
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '12px',
              color: atBat.role === 'batting' ? PALETTE.grass : PALETTE.dirt,
              letterSpacing: '1px',
              marginBottom: '4px',
            }}>
              {atBat.role === 'batting' ? 'YOU ARE BATTING!' : 'PEANUT GALLERY'}
            </p>
            {atBat.role === 'peanut_gallery' && (
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '11px',
                color: `${PALETTE.ink}88`,
                marginBottom: '6px',
              }}>
                Guess along for secret Peanut Points!
              </p>
            )}
            <TimerChip seconds={secs} testId="answer-timer" />
          </div>
          {atBat.questions && (
            <>
              <QuestionCategory
                label="TITLE"
                options={atBat.questions.title_options}
                selected={answers.title}
                onSelect={(v) => { playClick(); setAnswers((a) => ({ ...a, title: v })); }}
              />
              <QuestionCategory
                label="ARTIST"
                options={atBat.questions.artist_options}
                selected={answers.artist}
                onSelect={(v) => { playClick(); setAnswers((a) => ({ ...a, artist: v })); }}
              />
              <QuestionCategory
                label="ALBUM"
                options={atBat.questions.album_options}
                selected={answers.album}
                onSelect={(v) => { playClick(); setAnswers((a) => ({ ...a, album: v })); }}
              />
              <QuestionCategory
                label="YEAR"
                options={atBat.questions.year_options}
                selected={answers.year}
                onSelect={(v) => { playClick(); setAnswers((a) => ({ ...a, year: v })); }}
              />
            </>
          )}
          <RetroButton
            variant={atBat.role === 'batting' ? 'primary' : 'secondary'}
            size="md"
            fullWidth
            onClick={submitAnswers}
            data-testid="submit-answers-btn"
          >
            {atBat.role === 'batting' ? 'SWING!' : 'LOCK IN ANSWERS'}
          </RetroButton>
        </div>
      )}
    </div>
  );
}
